<template>
  <div
    class="section-result-row"
    ref="sectionResultRow"
    :class="`-mode-${$root.mode}`"
  >
    <p class="displayed-range-description">
      {{ $getTranslation("Displaying", $route.params.region) }} {{ displayingStart }} - {{ displayingEnd }} {{ $getTranslation("of",$route.params.region) }}
      <strong>{{ cSection.results.length }}</strong> {{
        cSection.results.length === 1 ? $getTranslation("result", $route.params.region) : $getTranslation("results", $route.params.region)
      }}
    </p>

    <div
      class="slider"
      ref="sectionResultRowSliderContainer"
      :class="`-section-type-${cSection.type}`"
    >
      <component
        v-for="(item, index) in cSection.results"
        :key="item.id"
        :is="`search-result-type-${cSection.type}`"
        :item="item"
        :is-last="index === cSection.results.length - 1"
      ></component>
    </div>

    <paginate
      v-if="cSection.results.length > resultsPerPage"
      v-model="cSection.page"
      :page-count="pageCount"
      :click-handler="onClickChangePage"
      :prev-text="`<button type='button' class='btn navigation-icon left-caret-alt slick-slider-button-prev'></button>`"
      :next-text="`<button type='button' class='btn navigation-icon right-caret-alt slick-slider-button-next'></button>`"
      container-class="search-section-pagination"
    >
    </paginate>
  </div>
</template>

<script>
import SectionResultTypes from "./section-result-types";
import "slick-carousel"; // https://kenwheeler.github.io/slick/
import Paginate from "vuejs-paginate";

const HORIZONTAL_TYPES = ["product", "document", "video"];

export default {
  props: ["section"],
  data() {
    return {
      slider: null,
      cSection: this.section,
      displayingStart: 1,
      displayingEnd: 4,
    };
  },
  components: {
    ...SectionResultTypes,
    Paginate,
  },
  watch: {
    section(v) {
      this.cSection = v;
    },
    "$root.mode"() {
      this.rebuildSlider();
    },
  },
  computed: {
    pageCount() {
      return Math.ceil(this.cSection.results.length / this.resultsPerPage);
    },
    resultsPerPage() {
      if (["xs", "sm"].includes(this.$root.mode)) {
        return 2;
      } else if (["md"].includes(this.$root.mode)) {
        return 3;
      } else {
        return 4;
      }
    },
  },
  mounted() {
    this.rebuildSlider();
  },
  created() {
    bus.$on("slick-slider:slide-height-changed", () => {
      this.$nextTick(() => {
        const $cont = $(this.$refs.sectionResultRowSliderContainer);

        // if the slick list has a set height (it does only in vertical mode), recalculate it based on the active slides, since slick doesn't handle slide resizes in vertical mode
        if (/height/.test($cont.find(".slick-list").attr("style") || "")) {
          const height = $cont
            .find(".slick-slide.slick-active")
            .get()
            .reduce((s, el) => s + $(el).outerHeight(), 0);

          if (height > 0) {
            $cont.find(".slick-list").css("height", height);
          }
        }

        // normalize all the slide heights in the slider to the tallest slide
        const tallestHeight = $cont
          .find(".slick-slide")
          .get()
          .reduce((s, el) => {
            if (s < $(el).outerHeight()) {
              s = $(el).outerHeight();
            }

            return s;
          }, 0);

        $cont.find(".slick-slide").css("height", tallestHeight);
      });
    });
  },
  methods: {
    rebuildSlider() {
      const rebuild = (initEventHandlers = false) => {
        const $cont = $(this.$refs.sectionResultRowSliderContainer);

        const shouldBeVertical =
          !HORIZONTAL_TYPES.includes(this.cSection.type) ||
          this.resultsPerPage <= 2;

        this.slider = $cont.slick({
          arrows: false,
          adaptiveHeight: true,
          infinite: false,
          draggable: this.cSection.results.length > this.resultsPerPage,
          slidesToShow: this.resultsPerPage,
          slidesToScroll: this.resultsPerPage,
          vertical: shouldBeVertical,
          verticalSwiping: shouldBeVertical,
          swipe: !shouldBeVertical,
        });

        if (initEventHandlers) {
          this.slider
            .on("afterChange", (event, slick, direction) => {
              this.refreshPaginationData();
            })
            .on("setPosition", () => {
              bus.$emit("slick-slider:slide-height-changed");
            })
            .on("destroy", () => {
              rebuild();
            });
        }

        this.refreshPaginationData();
      };

      if (this.slider) {
        this.slider.slick("unslick");
      } else {
        rebuild(true);
      }
    },
    refreshPaginationData() {
      this.cSection.page =
        Math.ceil(
          this.slider.slick("slickCurrentSlide") / this.resultsPerPage
        ) + 1;
      this.displayingStart =
        $(this.$refs.sectionResultRowSliderContainer)
          .find(".slick-active:first")
          .data("slick-index") + 1 | 0;
      this.displayingEnd =
        $(this.$refs.sectionResultRowSliderContainer)
          .find(".slick-active:last")
          .data("slick-index") + 1 | 0;
    },
    onClickChangePage(page) {
      if (this.slider) {
        this.slider.slick("slickGoTo", (page - 1) * this.resultsPerPage);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~slick-carousel/slick/slick.css";
@import "../../../css/abstracts/colors";

.displayed-range-description {
  margin-bottom: 2.5625rem;
}

.-mode-xs,
.-mode-sm,
.-mode-md {
  .displayed-range-description {
    margin-bottom: 1.6875rem;
  }
}

.slider {
  &.-section-type-video,
  &.-section-type-product,
  &.-section-type-document {
    margin: -0.75rem;
  }
}
</style>

<style lang="scss">
// this separate styles section only exists because slick slider injects dom elements that do not have the vue component data attributes (so scoped styles will not work)
.section-result-row {
  .slick-track {
    // force slides to display on the left
    margin-left: inherit !important;
  }
}

.slick-slide {
  height: auto;

  > div {
    height: 100%;
  }
}
</style>